/**
 * @fileoverview `@puffin-core/phone-ui/lazy` — the ONE lazy flavor of `<PhoneInput>`.
 *
 * PERF-FECHUNK-001 (2026-09-28): `PhoneInput` → `@puffin-core/phone` → libphonenumber-js is
 * ~300 KB minified. An app whose route tree reaches a phone field STATICALLY drags all of it
 * into the eager `vendor-misc-app` chunk — measured blowing the budget in 9 of 10 web apps the
 * day page-shell's ProfileEditDrawer went static, and again in port-web (11 statically-routed
 * consumers) and purchase-web's storefront widget after their PhoneInput conversions. This
 * subpath is the sanctioned boundary: importing it costs only this wrapper; the field's real
 * chunk loads on first RENDER (a user-action surface — a drawer, a dialog, a wizard step), which
 * the vendor classifier's reachability guard then keeps lazy by construction.
 *
 * Composition contract: identical props to `<PhoneInput>` (it spreads through). The Suspense
 * fallback reserves the field's footprint (label line + input box) so the swap-in never shifts
 * layout — the P1b `.status-slot` lesson applied to a form field. The label line shows the field's
 * own label (TOOLING-AND-DOCS-GEMS U12), so the label does not flash in on resolve. The placeholder
 * is not a control: the box is an `aria-hidden` block sized by `.form-input` (one line), so there is
 * no unlabelled field for a screen reader to land on, and no input to take keystrokes the real field
 * would discard (it used to be a label-less disabled `<input>`, an axe `label` violation whenever a
 * page was audited before the chunk resolved). Pass `loadingTestId` when a
 * suite needs to await the resolved field deterministically.
 *
 * A consumer whose page is ALREADY route-lazy (palm's ProfilePage) or whose budgets were
 * measured with the static import (pos-web) may keep `@puffin-core/phone-ui` proper; this
 * flavor exists for statically-reachable surfaces.
 *
 * @module @puffin-core/phone-ui/lazy
 */

import { lazy, Suspense } from 'react';

const PhoneInput = lazy(() =>
  import('./components/PhoneInput.jsx').then((m) => ({ default: m.PhoneInput })),
);

/**
 * The reserved footprint while the field's chunk loads — no layout shift on open. Exported so a
 * suite can render it DIRECTLY: under jest's transform the Suspense window can close within a
 * microtask, so the wrapper cannot pin the fallback deterministically. A label-less disabled
 * `<input>` here failed axe's `label` rule on port-web's endpoint-route drawer the moment its
 * target became a phone field (PLN-DEPT-GRAIN-003 Q-DOCS-1 residue, 2026-09-29).
 *
 * @param {{testId: string, label: string}} props
 * @returns {React.JSX.Element}
 */
export function PhoneFieldLoading({ testId, label }) {
  return (
    <div className="form-group" aria-busy="true" data-testid={testId}>
      <span className="form-label">{label}</span>
      <div className="form-input" aria-hidden="true">
        &nbsp;
      </div>
    </div>
  );
}

/**
 * `<PhoneInput>` behind its own chunk boundary. Props pass through unchanged.
 *
 * @param {import('./components/PhoneInput.jsx').PhoneInputProps & {loadingTestId?: string}} props -
 *   Everything `<PhoneInput>` takes, plus `loadingTestId` (default `'phone-input-loading'`), the
 *   fallback's testid
 * @returns {React.JSX.Element}
 */
export function PhoneInputLazy({ loadingTestId = 'phone-input-loading', ...props }) {
  return (
    <Suspense fallback={<PhoneFieldLoading testId={loadingTestId} label={props.label} />}>
      <PhoneInput {...props} />
    </Suspense>
  );
}
